<script setup lang="ts">
import { computed, nextTick, onMounted, provide, ref, watch } from 'vue';
import { usePhoneCsvStore } from '../../../stores';
import { closeModal, updateShow } from '../../../app/components/modal/modals';
import type { ModalProps } from '../../../app/components/modal/modals';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtModal } from '@dialpad/dialtone/vue3/lib/modal';
import { DATA_QA } from './dnc-rename.constants';
import { DATA_QA as MODAL_WRAPPER_DATA_QA } from '@/app/components/modal/modal-wrapper.constants';
import logger from '@/logger';
import DncNameInput from '../DncNameInput/DncNameInput.vue';
import { showAutoDismissToast, showErrorToast } from '@/noto';
import { useI18N } from '@dialpad/i18n';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';

const log = logger.getChild('DncRename');
log.debug('Mounting');

const props = defineProps<ModalProps>();

if (!props.meta?.id) {
  void closeModal();
  throw new Error('Missing DNC ID');
}

const { $t } = useI18N();

const phoneCsvStore = usePhoneCsvStore();

const dncCsv = phoneCsvStore.watch(() => props.meta?.id ?? '', 'DncRename');
const mounted = ref(false);

const validationTrigger = ref(0);
provide(TRIGGER_FORM_VALIDATION, validationTrigger);
const dncNameError = ref<string | null>(null);

const name = ref('');

watch(
  () => dncCsv.value.error,
  (error) => {
    if (error) {
      log.log('Error fetching DNC CSV', error);
      void closeModal();
    }
  },
);

watch(
  [() => dncCsv.value.item, () => dncCsv.value.loading],
  ([dncCsvToRename, loading]) => {
    if (!loading && !dncCsvToRename) {
      log.log('DNC CSV was deleted');
      void closeModal();
    }
  },
);

const confirm = async () => {
  log.debug('Confirm');
  if (!dncCsv.value.item?.id) {
    log.error('DNC CSV ID is missing');
    return;
  }

  validationTrigger.value++;
  await nextTick();

  if (dncNameError.value) {
    log.debug('Name is invalid');
    return;
  }

  void closeModal();
  log.debug('Renaming DNC CSV', dncCsv.value.item.id);
  phoneCsvStore.update(
    dncCsv.value.item.id,
    { name: name.value },
    {
      onSuccess: () => {
        void showAutoDismissToast({
          content: {
            title: $t('DNC_RENAME_SUCCESS_TITLE'),
          },
        });
      },
      onRetry: () => {},
      onError: (error) => {
        log.log('Error renaming DNC CSV', error);
        void showErrorToast({
          content: {
            title: $t('DNC_RENAME_ERROR_TITLE'),
          },
        });
      },
    },
  );
};

const cancel = () => {
  log.debug('Cancel');
  void closeModal();
};

watch(
  () => dncCsv.value.item?.name,
  () => {
    name.value = dncCsv.value.item?.name ?? '';
  },
  { immediate: true },
);

onMounted(() => {
  log.debug('Mounted');
  mounted.value = true;
});
const show = computed(() => mounted.value && !!dncCsv.value.item);
</script>
<template>
  <DtModal
    :data-qa="MODAL_WRAPPER_DATA_QA.DIALOG"
    :title="$t('DNC_RENAME_MODAL_TITLE')"
    :close-button-props="{
      ariaLabel: $t('MODAL_CLOSE_ARIA_LABEL'),
      'data-qa': MODAL_WRAPPER_DATA_QA.CLOSE,
    }"
    :show="show"
    content-class="d-p4 d-mn4"
    @update:show="updateShow"
  >
    <div>
      <p class="d-fs-100 d-fc-tertiary">
        {{ $t('DNC_NAME_INPUT_MAX_CHARACTERS') }}
      </p>
      <DncNameInput
        v-model="name"
        label=" "
        :show-description="false"
        @update:error="dncNameError = $event"
      />
    </div>
    <footer class="d-d-flex d-jc-flex-end d-mt16">
      <DtButton :data-qa="DATA_QA.CANCEL" importance="clear" @click="cancel">
        {{ $t('DNC_RENAME_CANCEL') }}
      </DtButton>
      <DtButton
        :data-qa="DATA_QA.CONFIRM"
        importance="primary"
        class="d-ml6"
        @click="confirm"
      >
        {{ $t('DNC_RENAME_CONFIRM') }}
      </DtButton>
    </footer>
  </DtModal>
</template>
